import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { IconX } from '@tabler/icons-react';
import toast from 'react-hot-toast';

import CardOurClassImage from 'components/CardOurClassImage';
import Modal from 'components/Modal';
import SelectedParticipant from './SelectedParticipant';
import { AgeDefault } from 'lib/DefaultData';
import { findStatus } from 'lib/findStatus';
import { formatTimeDisplay, calculateDurationMinutes, formatPickDate } from 'lib/timeHelpers';
import { API_URL, post } from 'lib/network/api';

const ModalBooking = ({
  schedule,
  selectedMembership,
  openModal,
  setOpenModal,
  onAuthRequired,
}) => {
  const navigate = useNavigate();
  const userProfile = useSelector((state) => state.authProfile.data);
  const [loading, setLoading] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState([]);

  const handleAuthRequired = () => {
    setOpenModal(false);
    onAuthRequired(true);
    toast.error('Your session has expired. Please sign in to continue.');
  };

  const handleConfirmBooking = async () => {
    if (!schedule?.id) {
      toast.error('Schedule not found');
      return;
    }

    if (selectedParticipant.length === 0) {
      toast.error('Please select at least one participant');
      return;
    }

    if (!selectedMembership) {
      toast.error('Please select a membership');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        user_id: userProfile?.id,
        class_schedule_id: schedule?.id,
        participant_ids: selectedParticipant.map((p) => p.id),
        membership_id: selectedMembership.id,
      };

      await post(`${API_URL}/api/event/class/booking`, payload);
      toast.success('Class booked successfully!');
      navigate('/my-classes');
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error?.response?.data?.message || 'Failed to book class');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={openModal} onRequestClose={() => setOpenModal(false)} clickOverlay={true}>
      <div className="bg-white font-sans rounded-2xl w-[340px] md:w-full md:max-w-[570px] min-w-0 relative text-kin-text flex flex-col gap-3 p-4 sm:p-5">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h2 className="text-base font-semibold text-kin-text">Your Booking</h2>
          <button type="button" onClick={() => setOpenModal(false)}>
            <IconX className="w-6 h-6" />
          </button>
        </div>

        {/* Class info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="hidden md:block">
            <div className="relative w-full flex gap-2 snap-x snap-mandatory overflow-x-auto z-0">
              <CardOurClassImage item={schedule?.class} variant="modal" />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm uppercase">{schedule?.class?.subject}</span>
            <h3 className="text-lg font-bold leading-none">{schedule?.class?.name || '-'}</h3>
            <span className="text-base font-semibold">{schedule?.class?.theme || '-'}</span>
            <span className="text-sm">
              {schedule?.class?.age_group
                ? findStatus(schedule?.class?.age_group, AgeDefault)
                : '-'}
            </span>

            <div className="flex items-center gap-2">
              <img src="/kin/assest/image/icon-calender.svg" alt="calendar" className="w-4 h-4" />
              <span className="text-sm">
                {schedule?.start_date ? formatPickDate(schedule.start_date) : '-'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <img src="/kin/assest/image/icon-clock.svg" alt="clock" className="w-4 h-4" />
              <span className="text-sm">
                {`${schedule?.start_time ? formatTimeDisplay(schedule.start_time) : '-'} - ${
                  schedule?.end_time ? formatTimeDisplay(schedule.end_time) : '-'
                } (${calculateDurationMinutes(
                  schedule?.start_time,
                  schedule?.end_time,
                  schedule?.class?.duration,
                )})`}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <img src="/kin/assest/image/icon-star.svg" alt="classroom" className="w-4 h-4" />
              <span className="text-sm capitalize">
                Classroom {schedule?.classroom?.name || '-'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <img src="/kin/assest/image/icon-user.svg" alt="teacher" className="w-4 h-4" />
              <span className="text-sm capitalize">{schedule?.teacher?.name || '-'}</span>
            </div>
          </div>
        </div>

        <div className="border-b border-kin-border/40 my-4" />

        {/* Participants */}
        <div className="flex flex-col gap-1">
          <span className="text-base font-semibold">Who will be attending this class?</span>
        </div>
        <SelectedParticipant
          value={selectedParticipant}
          onChange={setSelectedParticipant}
          onAuthRequired={handleAuthRequired}
          maxQuota={selectedMembership?.class_quota}
        />

        {/* Membership summary */}
        <div className="mt-4 flex flex-col gap-2">
          <span className="text-base font-semibold">Selected Class Pass</span>

          {!selectedMembership ? (
            <div className="rounded-xl border border-dashed border-gray-300 p-3 text-sm text-gray-500 text-center">
              No Class Pass selected — please choose one before booking.
            </div>
          ) : (
            <div className="rounded-xl border border-secondary-main/50 bg-secondary-main/5 p-4 flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-kin-text">
                  {selectedMembership.package_name || 'Class Pass'}
                </span>
                <span className="text-sm text-kin-text/80">
                  {selectedMembership.class_quota
                    ? `${selectedMembership.class_quota} ${
                        selectedMembership.class_quota > 1 ? 'Classes Left' : 'Class Left'
                      }`
                    : '-'}
                </span>
              </div>

              <div className="text-xs text-kin-text/70 flex flex-col">
                <span>
                  Active Until:{' '}
                  <strong>
                    {selectedMembership.active_until
                      ? format(selectedMembership.active_until, 'MMM dd, yyyy')
                      : '-'}
                  </strong>
                </span>
                <span>Status: {selectedMembership.status === '0' ? 'ACTIVE' : '-' || '-'}</span>
              </div>
            </div>
          )}
        </div>

        {/* Confirm Button */}
        <button
          onClick={handleConfirmBooking}
          disabled={loading || !selectedMembership}
          className={`mt-4 inline-flex items-center justify-center rounded-full px-4 py-3 text-sm font-semibold text-white transition
            ${
              loading || !selectedMembership
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-primary-main hover:bg-primary-dark'
            }
          `}>
          {loading ? 'BOOKING...' : 'CONFIRM CLASS BOOKING'}
        </button>
      </div>
    </Modal>
  );
};

export default ModalBooking;
